import { trpc } from "@client/trpc";
import { useEffect, useMemo } from "react";
import {
  isRemovedFromCatalog,
  keepCachedDuration,
  keptNewerStamp,
  type MobileVideo,
} from "../lib/mobileVideoStore";
import { useVideoStore } from "../lib/VideoStoreContext";
import { useNetworkStatus } from "./useNetworkStatus";

type Args = {
  roundId: string;
  persistedItems: MobileVideo[];
};

type Result = {
  videos: MobileVideo[];
  isLoading: boolean;
  error: string | null;
};

export function useVideoCatalog({ roundId, persistedItems }: Args): Result {
  const store = useVideoStore();
  const { isOnline } = useNetworkStatus();
  const query = trpc.getVideos.useQuery({ roundId }, { enabled: isOnline });

  const videos = useMemo<MobileVideo[]>(() => {
    const persistedById = new Map(
      persistedItems.map((item) => [item.id, item]),
    );

    if (isOnline && query.data) {
      return query.data.map((video) => {
        const persisted = persistedById.get(video.id);

        return {
          id: video.id,
          roundId,
          type: video.type,
          // The unit IS the transcript now; its own id addresses the transcript
          // content, but only when the unit actually carries transcript text.
          // `null` tells the download engine there's nothing to fetch/cache.
          transcriptId: video.hasTranscript ? video.id : null,
          transcriptContent: persisted?.transcriptContent ?? null,
          // The catalog doesn't carry chapters; keep any offline-cached set.
          chapters: persisted?.chapters ?? null,
          track: video.track,
          // The server's answer WINS, including when it is null. Falling back to
          // the cached id here would resurrect a home that no longer exists —
          // untag the recommender, or make them inactive, and the queue would
          // keep linking to a page the video has left. The cached value is for
          // the offline branch below, where there is no server answer at all.
          homeRecommenderId: video.homeRecommenderId,
          uploadedAt: video.uploadedAt,
          title: video.filename,
          description: "",
          filename: video.filename,
          contentType: video.contentType,
          sizeBytes: video.sizeBytes,
          downloadSizeBytes: video.downloadSizeBytes,
          isAvailable: video.status === "READY",
          // Server duration wins; otherwise fall back to one this device
          // learned from playback so the row doesn't show a file size until
          // the next refetch. Value and pending flag both apply only while
          // the row still describes the generation they were learned from —
          // a replaced media file must not inherit the old duration.
          durationSeconds:
            video.durationSeconds ??
            (persisted != null &&
            keepCachedDuration(persisted, video.fileUploadedAt)
              ? (persisted.durationSeconds ?? null)
              : null),
          fileUploadedAt: video.fileUploadedAt,
          ...(video.durationSeconds == null &&
          persisted?.durationSecondsPendingReport === true &&
          keepCachedDuration(persisted, video.fileUploadedAt)
            ? { durationSecondsPendingReport: true }
            : {}),
          ...(video.durationSeconds == null && persisted != null
            ? keptNewerStamp(persisted, video.fileUploadedAt)
            : {}),
          transcriptToVideoOffsetSeconds:
            video.transcriptToVideoOffsetSeconds ?? 0,
          attendeeCount: video.attendeeCount,
          activePosterId: video.activePosterId,
          download: persisted?.download ?? null,
          watchProgress: persisted?.watchProgress ?? 0,
          progressClientUpdatedAt: persisted?.progressClientUpdatedAt ?? null,
        };
      });
    }

    if (!isOnline) {
      // Show everything we know about for this round — including videos that
      // haven't been downloaded — so the list stays addressable while
      // offline. Consumers gray out non-downloaded rows. Videos a previous
      // online sync flagged as removed from the catalog stay out, matching
      // what the server would return.
      return persistedItems.filter(
        (item) => item.roundId === roundId && !isRemovedFromCatalog(item),
      );
    }

    return [];
  }, [isOnline, query.data, roundId, persistedItems]);

  // Mirror the latest online catalog into the store so a subsequent offline
  // session can render the same list (and so `selectedVideoId` lookups
  // resolve for non-downloaded videos when the network drops). Passing the
  // roundId makes the sync authoritative for the round: cached videos the
  // server no longer returns get flagged as removed so queue/recent/saved
  // rows can say so instead of dead-ending.
  useEffect(() => {
    // Gate on the query having actually resolved (not on videos.length):
    // while loading, `videos` is [] and syncing it would wrongly flag the
    // whole round as removed — but a genuinely emptied round (server
    // returned []) must still sync so its cached videos get flagged.
    if (!isOnline || query.data == null) {
      return;
    }

    store.syncCatalogMetadata(videos, roundId);
  }, [isOnline, query.data, videos, store, roundId]);

  return {
    videos,
    isLoading: isOnline ? query.isLoading : false,
    error: isOnline ? (query.error?.message ?? null) : null,
  };
}
